import { FC } from "react";

import { Button, Buttons, Callout, Message, Messages, Tag } from "capitalrx-components-2.0/lib/general";
import type { TagType } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";

import { drugTypeLabel, isExcludedTier, RawFormularyRecord } from "./DrugCoverage";
import { isProduction, isPrime } from "@/utils";
import styles from "./DrugCoverage.module.scss";

const COMMON_RESTRICTION_CODES = ["PA", "QL", "ST", "GL", "AL1", "ACA", "CK"];

const RESTRICTION_ABBREVIATION: Record<string, string> = {
  PA: "PA",
  QL: "QL",
  ST: "ST",
  GL: "GL",
  AL1: "AGE",
  ACA: "ACA",
  CK: "CK",
};

function restrictionTooltip(code: string, record: RawFormularyRecord): string {
  switch (code) {
    case "PA":
      return "Prior Authorization Required";
    case "QL":
      return record.quantity_limit ? `Quantity Limit: ${record.quantity_limit}` : "Quantity Limit";
    case "ST":
      return "Step Therapy applies. View drug details for more information.";
    case "GL":
      return record.gender_limit ? `Gender Limit: ${record.gender_limit}` : "Gender Limit";
    case "AL1":
      return record.age_limit_original ? `Age Limit: ${record.age_limit_original}` : "Age Limit";
    case "ACA":
      return "Affordable Care Act";
    case "CK":
      return "Compound Kit";
    default:
      return "";
  }
}

function commonRestrictionCodes(record: RawFormularyRecord): string[] {
  const edits = record.edits || [];
  return edits.filter((code) => COMMON_RESTRICTION_CODES.includes(code));
}

function tierTagLabel(record: RawFormularyRecord): string {
  if (isExcludedTier(record)) {
    return "Excluded";
  }
  const original = record.tier_original || "";
  if (original.includes("|")) {
    const parts = original.split("|");
    const number = parts[0].trim();
    const name = parts[1].trim();
    return `Tier ${number} - ${name}`;
  }
  return `Tier ${record.tier}`;
}

function tierTagType(record: RawFormularyRecord): TagType {
  if (isExcludedTier(record)) {
    return "info-high-contrast";
  }
  const name = record.tier_original || "";
  if (
    name.includes("Non-preferred") ||
    name.includes("Non-Preferred") ||
    name.includes("Specialty")
  ) {
    return "warning-high-contrast";
  }
  return "success-high-contrast";
}

const RestrictionList: FC<{ record: RawFormularyRecord }> = ({ record }) => {
  const codes = commonRestrictionCodes(record);

  if (codes.length === 0) {
    return <Body>None</Body>;
  }

  return (
    <Body>
      {codes.map((code, idx) => (
        <span key={code}>
          <Callout tooltip={restrictionTooltip(code, record)}>
            {RESTRICTION_ABBREVIATION[code]}
          </Callout>
          {idx < codes.length - 1 ? ", " : ""}
        </span>
      ))}
    </Body>
  );
};

interface DrugResultCardProps {
  record: RawFormularyRecord;
  isMobile: boolean;
  showDrugActions: boolean;
  showAlternativesAction?: boolean;
  onAlternativesClick?: () => void;
  className?: string;
}

export const DrugResultCard: FC<DrugResultCardProps> = ({
  record,
  isMobile,
  showDrugActions,
  showAlternativesAction = false,
  onAlternativesClick,
  className,
}) => {
  const drugType = drugTypeLabel(record);
  const showCustomMessage = typeof record.custom === "string" && record.custom.length > 0;
  const showNonFormularyMessage = isExcludedTier(record);
  const featureFlagShowDispensingChannel = !isProduction && !isPrime;

  return (
    <Stack spacing="medium" className={[styles.DrugCard, className].filter(Boolean).join(" ")}>
      {isMobile ? (
        <Stack spacing="mini">
          <Semibold className={styles.DrugName}>{record.label_name}</Semibold>
          <Shelf spacing="mini">
            {drugType && <Tag type="info">{drugType}</Tag>}
            <Tag type={tierTagType(record)}>{tierTagLabel(record)}</Tag>
          </Shelf>
        </Stack>
      ) : (
        <Shelf spacing="small">
          <Semibold className={styles.DrugName}>{record.label_name}</Semibold>
          <Shelf.Spacer />
          <Shelf spacing="mini">
            {drugType && <Tag type="info">{drugType}</Tag>}
            <Tag type={tierTagType(record)}>{tierTagLabel(record)}</Tag>
          </Shelf>
        </Shelf>
      )}

      {isMobile ? (
        <Stack spacing="mini">
          {featureFlagShowDispensingChannel && (
            <Shelf spacing="small">
              <Body size="mini"><Semibold className={styles.ColumnLabel}>Dispensing Channel:</Semibold></Body>
              <Body>{record.network || "--"}</Body>
            </Shelf>
          )}
          <Shelf spacing="small">
            <Body size="mini"><Semibold className={styles.ColumnLabel}>Quantity Limit:</Semibold></Body>
            <Body>{record.quantity_limit || "--"}</Body>
          </Shelf>
          <Shelf spacing="small">
            <Body size="mini"><Semibold className={styles.ColumnLabel}>Restrictions:</Semibold></Body>
            <RestrictionList record={record} />
          </Shelf>
        </Stack>
      ) : (
        <Shelf spacing="large" className={styles.ColumnsShelf}>
          {featureFlagShowDispensingChannel && (
            <Stack spacing="none" className={styles.Column}>
              <Body size="mini"><Semibold className={styles.ColumnLabel}>Dispensing Channel</Semibold></Body>
              <Body>{record.network || "--"}</Body>
            </Stack>
          )}
          <Stack spacing="none" className={styles.Column}>
            <Body size="mini"><Semibold className={styles.ColumnLabel}>Quantity Limit</Semibold></Body>
            <Body>{record.quantity_limit || "--"}</Body>
          </Stack>
          <Stack spacing="none" className={styles.Column}>
            <Body size="mini"><Semibold className={styles.ColumnLabel}>Restrictions</Semibold></Body>
            <RestrictionList record={record} />
          </Stack>
        </Shelf>
      )}

      {showNonFormularyMessage && (
        <Message appearance={Messages.Informational} visible>
          Drug not in formulary but eligible for PA review
        </Message>
      )}

      {showCustomMessage && (
        <Message appearance={Messages.Informational} visible>
          This drug has unique restrictions: Not covered via pharmacy benefit - purchase over the counter
        </Message>
      )}

      {(showDrugActions || showAlternativesAction) && (
        <Shelf spacing="small">
          {showDrugActions && <Button appearance={Buttons.Functional.Tertiary.Small}>Details</Button>}
          {showAlternativesAction && (
            <Button appearance={Buttons.Functional.Tertiary.Small} onClick={onAlternativesClick}>
              Alternatives
            </Button>
          )}
          <Shelf.Spacer />
          {showDrugActions && (
            <Button appearance={Buttons.Functional.Tertiary.Small}>Check Coverage</Button>
          )}
        </Shelf>
      )}
    </Stack>
  );
};
